import React, { FC, PropsWithChildren, useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnyObjectSchema } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { AddButton } from "@/components/atoms";

import { StyledActionGroup, StyledFormPanelHeader, StyledInfosContainer, StyledTitle } from "../../styled";
import {
    asSchema,
    defaultFormSchema,
    defaultValues,
    formMapper,
    FormPanelInfo,
    mapGroupComponents,
    mapGroupDefaultVisibility,
    MapSchema,
} from "../../utils";
import InfoFieldEdit from "../InfoFieldEdit/InfoFieldEdit";
import { StyledButtonCancel, StyledButtonSave, StyledCloseIcon, StyledGroup, StyledGroupField } from "./styled";

type FormPanelEditProps = {
    title?: string;
    validationSchema?: AnyObjectSchema;
    panelInfo: FormPanelInfo;
    isLoading?: boolean;
    onSubmit: (values) => void;
    onClose: () => void;
};

const FormPanelEdit: FC<PropsWithChildren<FormPanelEditProps>> = ({
    title,
    validationSchema,
    panelInfo,
    isLoading = false,
    onSubmit,
    onClose,
    children,
}) => {
    const { t } = useTranslation();
    const { data, groups } = panelInfo;
    let groupDefaultVisibility = {};
    let groupComponents = {};

    if (Object.keys(groups).length !== 0) {
        groupComponents = mapGroupComponents(data, groups);
        groupDefaultVisibility = mapGroupDefaultVisibility(groupComponents);
    }

    const [displayedGroups, setDisplayedGroups] = useState(groupDefaultVisibility);

    const formSchema = asSchema(defaultFormSchema(data));

    type FormTypes = MapSchema<typeof formSchema>;
    const {
        control,
        handleSubmit,
        setValue,
        formState: { isDirty },
    } = useForm<FormTypes>({
        mode: "onBlur",
        defaultValues: defaultValues(data),
        resolver: validationSchema && yupResolver(validationSchema),
    });

    const handleSaveEdit = (formData: FormTypes) => {
        onSubmit(formMapper<FormTypes, typeof formSchema>(formData, formSchema));
        onClose();
    };

    const renderField = (field) => (
        <InfoFieldEdit key={`field-edit-${field.id}`} label={field.label} field={field} control={control} />
    );

    const handleAddGroupField = (key: string) => {
        const lastIndexFalse = displayedGroups[key].indexOf(false);
        displayedGroups[key][lastIndexFalse] = true;
        displayedGroups[key] = displayedGroups[key].sort((x, y) => y - x);
        setDisplayedGroups({ ...displayedGroups });
    };

    const handleRemoveGroupField = (key: string, index) => {
        displayedGroups[key][index] = false;
        displayedGroups[key] = displayedGroups[key].sort((x, y) => y - x);
        groups[key][index].forEach((field) => setValue(field, "", { shouldDirty: true, shouldValidate: false }));

        setDisplayedGroups({ ...displayedGroups });
    };

    const renderGroupField = (group, key: string) =>
        group.map((field, index) => {
            const componentFields = field.map((insideField) => renderField(insideField));

            return (
                <StyledGroupField $hide={!displayedGroups[key][index]} key={`group-${(field[0] || field).id}`}>
                    {componentFields}
                    {index !== 0 && <StyledCloseIcon onClick={() => handleRemoveGroupField(key, index)} />}
                </StyledGroupField>
            );
        });

    const renderGroupInfo = () => {
        const renderComponents = [];
        Object.keys(groupComponents).forEach((key) => {
            renderComponents.push(
                <StyledGroup key={`group-${key}`}>
                    {renderGroupField(groupComponents[key], key)}
                    {groupComponents[key].length > 1 && (
                        <AddButton
                            disabled={!displayedGroups[key].includes(false)}
                            data-testid={`add-button-${key}`}
                            label={t(`Common.OneMore${key[0].toUpperCase()}${key.slice(1)}`)}
                            onClick={() => handleAddGroupField(key)}
                        />
                    )}
                </StyledGroup>,
            );
        });
        return renderComponents;
    };

    const renderInfo = () => {
        return data.filter((field) => !field.group).map((field) => renderField(field));
    };

    return (
        <>
            <StyledFormPanelHeader>
                <StyledTitle>{title}</StyledTitle>
                <StyledActionGroup>
                    <StyledButtonCancel data-testid="form_panel-button-close" onClick={onClose} variant="outlined">
                        {t("Common.Cancel")}
                    </StyledButtonCancel>
                    <StyledButtonSave
                        data-testid="form_panel-button-save"
                        classes={{ disabled: "disabled" }}
                        onClick={handleSubmit(handleSaveEdit)}
                        disabled={!isDirty || isLoading}
                    >
                        {t("Common.Save")}
                    </StyledButtonSave>
                </StyledActionGroup>
            </StyledFormPanelHeader>
            {children}
            <StyledInfosContainer>
                {renderGroupInfo()}
                {renderInfo()}
            </StyledInfosContainer>
        </>
    );
};

export default FormPanelEdit;
